// VideoHero.tsx
'use client'

import React, { useRef, useState } from 'react'
import { PortableTextObject } from 'sanity'
import { PortableText, PortableTextComponents } from 'next-sanity'
import { AppText } from '@otomoro/components/shared/AppText'
import { styles, VideoHeroVariants } from './VideoHero.css'

interface VideoHeroProps extends VideoHeroVariants {
  title: PortableTextObject
  description?: PortableTextObject
  videoUrl: string
}

export const VideoHero: React.FC<VideoHeroProps> = ({ title, description, videoUrl }) => {
  const {
    base,
    videoWrapper,
    video: videoStyle,
    overlay,
    content,
    title: titleStyle,
    titlePink,
    description: descriptionStyle,
  } = styles()

  const [playing, setPlaying] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const handlePlay = () => {
    setPlaying(true)
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ method: 'play' }),
      '*'
    )
  }

  const titleComponents: PortableTextComponents = {
    block: {
      normal: ({ children }) => <h2 className={titleStyle()}>{children}</h2>,
    },
    marks: {
      pink: ({ children }) => <span className={titlePink()}>{children}</span>,
    },
  }

  const descriptionComponents: PortableTextComponents = {
    block: {
      normal: ({ children }) => (
        <AppText as="p" size="intro" className={descriptionStyle()}>
          {children}
        </AppText>
      ),
    },
  }

  return (
    <section className={base()}>
      <div className={videoWrapper()}>
        <iframe
          ref={iframeRef}
          className={videoStyle()}
          src={videoUrl}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </div>

      {!playing && <div className={overlay()} />}

      {!playing && (
        <div className={content()}>
          <PortableText value={title} components={titleComponents} />
          {description && <PortableText value={description} components={descriptionComponents} />}
          {/* hidden below lg, shows only on desktop */}
          <button
            onClick={handlePlay}
            className="hidden lg:flex mt-4 items-center justify-center w-24 h-24 rounded-full bg-[#FF77A8] bg-opacity-90 hover:bg-opacity-100 transition"
            aria-label="Play video"
          >
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="white" fillOpacity="0.15" />
              <polygon points="19,16 36,24 19,32" fill="white" />
            </svg>
          </button>
        </div>
      )}
    </section>
  )
}
